import { create } from "zustand";
import { api, type PrInfo } from "../api/client";

/**
 * PR batch — interactive editor saves stage onto a shared draft branch
 * (`shelf/draft/<batchId>`) without opening a PR. The floating PR button then
 * opens one PR for the whole batch. After submitting, a fresh batch id is
 * minted so the next round of edits starts clean.
 *
 * The batch id is persisted so it survives reloads; `files` is a client-side
 * cache of staged docs, reconciled against the server via refresh().
 */

const BATCH_KEY = "shelf-pr-batch-id";

function newBatchId(): string {
  const rand = (
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
  )
    .replace(/[^A-Za-z0-9]/g, "")
    .slice(0, 16);
  return rand || `${Date.now()}`;
}

function loadBatchId(): string {
  try {
    const existing = localStorage.getItem(BATCH_KEY);
    if (existing) return existing;
  } catch {
    /* localStorage unavailable */
  }
  const id = newBatchId();
  try {
    localStorage.setItem(BATCH_KEY, id);
  } catch {
    /* ignore */
  }
  return id;
}

function persistBatchId(id: string): void {
  try {
    localStorage.setItem(BATCH_KEY, id);
  } catch {
    /* ignore */
  }
}

interface BatchState {
  batchId: string;
  files: string[];
  pr: PrInfo | null;
  submitting: boolean;
  /** Locally record a doc that was just staged (optimistic; refresh confirms). */
  stage: (path: string) => void;
  /** Re-read the batch from the server (staged files + any open PR). */
  refresh: () => Promise<void>;
  /** Drop one doc from the batch. */
  remove: (path: string) => Promise<void>;
  /** Discard the whole batch (delete its draft branch), then start fresh. */
  discard: () => Promise<void>;
  /** Open the single PR for the batch, then start a fresh empty batch. */
  submit: (title?: string) => Promise<PrInfo | null>;
}

export const useBatchStore = create<BatchState>((set, get) => ({
  batchId: loadBatchId(),
  files: [],
  pr: null,
  submitting: false,

  stage: (path) =>
    set((s) => (s.files.includes(path) ? s : { files: [...s.files, path] })),

  refresh: async () => {
    const { batchId } = get();
    try {
      const status = await api.git.prStatus(batchId);
      set({ files: status.files, pr: status.pr });
    } catch {
      /* leave cached state on transient failure */
    }
  },

  remove: async (path) => {
    const { batchId } = get();
    try {
      const { files } = await api.git.excludeFromBatch({
        batch: batchId,
        path,
      });
      set({ files });
    } catch {
      /* keep current list on failure */
    }
  },

  discard: async () => {
    const { batchId } = get();
    set({ submitting: true });
    try {
      await api.git.discardBatch({ batch: batchId });
    } finally {
      // Rotate to a fresh batch regardless; the old draft branch is gone.
      const next = newBatchId();
      persistBatchId(next);
      set({ batchId: next, files: [], pr: null, submitting: false });
    }
  },

  submit: async (title) => {
    const { batchId } = get();
    set({ submitting: true });
    try {
      const { pr } = await api.git.openPr({ batch: batchId, title });
      // Rotate to a fresh batch so the next edits start clean.
      const next = newBatchId();
      persistBatchId(next);
      set({ batchId: next, files: [], pr: null, submitting: false });
      return pr;
    } catch (e) {
      set({ submitting: false });
      throw e;
    }
  },
}));
